import PocketBase from 'pocketbase';
import {
  db,
  SYNC_STATUS
} from '../database';
import { outboxManager, OUTBOX_ACTIONS } from './OutboxManager';
import { generateRemoteId, isPocketBaseId } from './ids';
import {
  resolveInbound,
  handlePushRejected
} from '../collaboration/ConflictResolver';

/**
 * SyncWorker — bidirectional delta sync engine between Dexie and PocketBase.
 *
 * Push: drains the OutboxManager queue FIFO with exponential backoff.
 * Pull: incremental reconciliation using a per-tenant `lastSyncTime`
 *       watermark stored in `db.meta`, plus live SSE subscriptions.
 *
 * Design invariants:
 *  - NEVER blocks the UI thread: async pipeline, bounded batches, no
 *    synchronous loops over large payloads.
 *  - Tenant isolation: every push/pull query is scoped to exactly one
 *    tenantId. The watermark is per-tenant (`syncCursor:<tenantId>`).
 *  - Optimistic locking on push via PocketBase `version` field; a rejection
 *    triggers ConflictResolver instead of overwriting the server.
 *  - SSE events are treated as hints only ("something changed — reconcile");
 *    correctness never depends on them. Reconnect + watermark pull is the
 *    authoritative catch-up path.
 */

const SYNC_CURSOR_KEY = 'syncCursor';
// Health-check cadence while the tab is visible. Background tabs get no
// timers at all — reconnect is driven by online/visibilitychange instead,
// because browsers throttle setInterval in background tabs anyway.
const HEALTH_CHECK_INTERVAL_MS = 60_000;

/** Map local collection name -> PocketBase collection name. */
const COLLECTION_MAP = {
  forms: 'forms',
  customers: 'customers',
  properties: 'properties'
};

export class SyncWorker {
  constructor({
    baseUrl,
    healthCheckIntervalMs = HEALTH_CHECK_INTERVAL_MS
  } = {}) {
    // Vite injects import.meta.env at build time; resolve lazily so the
    // module also loads under plain Node (tests) without a bundler.
    this.baseUrl =
      baseUrl ||
      (typeof import.meta !== 'undefined' && import.meta.env
        ? import.meta.env.VITE_POCKETBASE_URL
        : undefined) ||
      'http://127.0.0.1:8090';
    this.pb = new PocketBase(this.baseUrl);
    this.healthCheckIntervalMs = healthCheckIntervalMs;

    this.isRunning = false;
    this.isSyncing = false;
    /** @type {'idle'|'syncing'|'offline'|'error'} */
    this.status = 'idle';
    this.activeTenantId = null;
    this.lastSyncAt = null;
    this._healthTimer = null;
    this._unsubscribers = [];
    this._listeners = new Set();
  }

  // ---------------------------------------------------------------------
  // Listener plumbing for React (useSync) — no external state library
  // ---------------------------------------------------------------------

  onChange(cb) {
    this._listeners.add(cb);
    return () => {
      this._listeners.delete(cb);
    };
  }

  _emit() {
    const snapshot = {
      status: this.status,
      activeTenantId: this.activeTenantId,
      lastSyncAt: this.lastSyncAt ?? null
    };
    this._listeners.forEach((cb) => {
      try {
        cb(snapshot);
      } catch {
        /* UI listener errors must never break sync */
      }
    });
  }

  // ---------------------------------------------------------------------
  // Identity / tenant context
  // ---------------------------------------------------------------------

  /**
   * Set the active tenant context (called by TenantContext/AuthContext).
   * Switching tenants tears down SSE subscriptions and re-establishes them
   * for the new tenant's collections — never subscribe cross-tenant.
   */
  async setTenantContext(tenantId) {
    if (!tenantId) {
      throw new Error('SyncWorker.setTenantContext: tenantId required');
    }
    if (this.activeTenantId === tenantId) return;

    this.teardownSubscriptions();
    this.activeTenantId = tenantId;

    const cursor = await db.getMeta(`${SYNC_CURSOR_KEY}:${tenantId}`, 0);
    this.lastSyncAt = cursor;

    if (this.isRunning && navigator.onLine) {
      await this.subscribeToTenantCollections();
    }
    this._emit();
  }

  /** Stamp auth from AuthContext after login / token refresh. */
  setAuth(token, userModel = null) {
    this.pb.authStore.save(token, userModel ?? {});
    if (userModel?.organization_id && !this.activeTenantId) {
      void this.setTenantContext(userModel.organization_id);
    }
  }

  clearAuth() {
    this.pb.authStore.clear();
  }

  // ---------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------

  async start() {
    if (this.isRunning) return;
    this.isRunning = true;

    window.addEventListener(
      'online',
      (this._handleOnline = () => {
        this._emit();
        // Small delays let flapping radios settle before hammering PB.
        setTimeout(() => void this.reconcileNow('online'), 750);
        setTimeout(() => void this.reconcileNow('online'), 1500);
      })
    );
    window.addEventListener(
      'offline',
      (this._handleOffline = () => {
        this.status = 'offline';
        this._emit();
      })
    );
    document.addEventListener(
      'visibilitychange',
      (this._handleVisibility = () => {
        if (!document.hidden && navigator.onLine) {
          void this.reconcileNow('visible');
        }
      })
    );

    this._healthTimer = setInterval(() => {
      if (document.hidden || !navigator.onLine) return;
      void this.checkHealth().then((ok) => {
        if (ok && (this.status === 'offline' || this.status === 'error')) {
          void this.reconcileNow('health');
        } else if (!ok && this.status === 'idle') {
          this.status = 'error';
          this._emit();
        }
      });
    }, this.healthCheckIntervalMs);

    if (navigator.onLine) {
      const ok = await this.checkHealth();
      if (ok) await this.reconcileNow('boot');
      else this.status = 'error';
    } else {
      this.status = 'offline';
    }
    this._emit();
  }

  stop() {
    this.isRunning = false;
    clearInterval(this._healthTimer);
    window.removeEventListener('online', this._handleOnline);
    window.removeEventListener('offline', this._handleOffline);
    document.removeEventListener('visibilitychange', this._handleVisibility);
    this.teardownSubscriptions();
    // Return claimed-but-unpushed deltas so nothing is stranded IN_FLIGHT.
    void outboxManager.releaseInFlight();
  }

  // ---------------------------------------------------------------------
  // Health check
  // ---------------------------------------------------------------------

  async checkHealth() {
    try {
      await this.pb.health.check({ $autoCancel: false });
      return true;
    } catch {
      return false;
    }
  }

  // ---------------------------------------------------------------------
  // PUSH — drain outbox to PocketBase (FIFO, batched, backoff-aware)
  // ---------------------------------------------------------------------

  async pushOutbox() {
    if (this.isSyncing || !navigator.onLine) return { pushed: 0, failed: 0 };

    if (!(await this.checkHealth())) {
      this.status = navigator.onLine ? 'error' : 'offline';
      this._emit();
      return { pushed: 0, failed: 0 };
    }

    this.isSyncing = true;
    this.status = 'syncing';
    let pushed = 0;
    let failed = 0;

    try {
      // Outer loop: keep claiming until the queue is dry or an item defers
      // into backoff. Batches are bounded so a huge backlog can't hold a
      // transaction or an HTTP request open indefinitely.
      for (;;) {
        const batch = await outboxManager.claimBatch({ limit: 25 });
        if (batch.length === 0) break;

        let deferred = false;
        for (const item of batch) {
          try {
            await this.pushItem(item);
            await outboxManager.markDone(item.id);
            pushed++;
          } catch (error) {
            const outcome = await outboxManager.markFailed(item, error);
            failed++;
            if (outcome !== 'failed') {
              if (isAuthError(error)) {
                // Session expired mid-batch — bail; the token refresh flow
                // will restart us. Items stay pending with backoff.
                this.status = 'error';
                this._emit();
                return { pushed, failed };
              }
              if (isRetryable(error)) {
                deferred = true; // backoff scheduled — stop hammering now
              }
            }
            // Terminal failures continue with the next item.
          }
        }
        if (deferred) break;
      }
    } finally {
      this.isSyncing = false;
      // An auth failure set status='error' above — keep it visible to the
      // UI (token-refresh flow) instead of letting the finally reset it to
      // 'idle' as if nothing happened. Only a healthy drain reports idle.
      if (!(this.status === 'error' && failed > 0)) {
        this.status = navigator.onLine ? 'idle' : 'offline';
      }
      if (pushed > 0 || failed > 0) this._emit();
    }

    return { pushed, failed };
  }

  /**
   * Resolve the PocketBase record id for an outbox item's source record.
   *
   * forms          → `instanceId` (client-minted, PB-format after remint)
   * customers/props→ `remoteId` (client-minted PB-format id, Dexie v5)
   *
   * Legacy records (pre-PB-format ids / pre-v5 rows without remoteId) get a
   * fresh id minted here and persisted — safe because no server copy of
   * those records can exist (invalid ids could never have been pushed).
   *
   * @returns {Promise<{remoteId: string, record: Object|null}>}
   */
  async resolveRemoteIdentity(item) {
    const table = db.table(item.collection);
    const numericId = Number(item.recordId);
    const key = Number.isFinite(numericId) ? numericId : String(item.recordId);
    const record = await table.get(key);
    if (!record) {
      throw new Error(
        `SyncWorker: local ${item.collection} record ${item.recordId} vanished before push`
      );
    }

    let remoteId = item.collection === 'forms'
      ? record.instanceId
      : record.remoteId;

    if (!isPocketBaseId(remoteId)) {
      remoteId = generateRemoteId();
      await table.update(key, item.collection === 'forms'
        ? { instanceId: remoteId }
        : { remoteId });
    }

    return { remoteId, record };
  }

  /**
   * Push one outbox delta to PocketBase.
   *
   * CREATE pushes the full record with the client-minted id (upsert-style:
   * a duplicate id falls through to conflict resolution).
   * UPDATE patches by remote id; if the record was never pushed (404) it
   * degrades to a create. DELETE removes by remote id (404 = already done).
   */
  async pushItem(item) {
    const pbCollection = COLLECTION_MAP[item.collection] ?? item.collection;
    const { remoteId, record } = await this.resolveRemoteIdentity(item);
    const payload = mapLocalToRemote(item.collection, record, remoteId);

    switch (item.action) {
      case OUTBOX_ACTIONS.CREATE: {
        try {
          await this.pb
            .collection(pbCollection)
            .create(payload, { $autoCancel: false });
        } catch (err) {
          if (!isPreconditionFailed(err) && !isDuplicateKeyError(err)) throw err;
          // Server already has this record (or advanced past our base).
          await this.handlePushConflict(item, pbCollection, remoteId);
        }
        break;
      }
      case OUTBOX_ACTIONS.UPDATE: {
        try {
          await this.pb
            .collection(pbCollection)
            .update(remoteId, payload, { $autoCancel: false });
        } catch (err) {
          if (isNotFound(err)) {
            // Never pushed (CREATE collapsed) — degrade to create.
            await this.pb
              .collection(pbCollection)
              .create(payload, { $autoCancel: false });
          } else if (isPreconditionFailed(err) || isDuplicateKeyError(err)) {
            await this.handlePushConflict(item, pbCollection, remoteId);
          } else {
            throw err;
          }
        }
        break;
      }
      case OUTBOX_ACTIONS.DELETE: {
        try {
          await this.pb
            .collection(pbCollection)
            .delete(remoteId, { $autoCancel: false });
        } catch (err) {
          // Deleting something that doesn't exist remotely = already done.
          if (!isNotFound(err)) throw err;
        }
        break;
      }
      default:
        throw new Error(`SyncWorker.pushItem: unknown action ${item.action}`);
    }

    // Post-push bookkeeping regardless of which path succeeded.
    await stampSynced(item);
  }

  /**
   * Server rejected our push because it already holds a diverging copy for
   * this id / has advanced past our base version. Run the 3-way merge;
   * clean merges are written back immediately, real conflicts park the
   * record in CONFLICT state for the ConflictResolverModal review flow.
   */
  async handlePushConflict(item, pbCollection, remoteId) {
    const numericId = Number(item.recordId);
    const key = Number.isFinite(numericId) ? numericId : String(item.recordId);
    const [serverRecord, localRecord] = await Promise.all([
      this.pb
        .collection(pbCollection)
        .getOne(remoteId, { $autoCancel: false }),
      db.table(item.collection).get(key)
    ]);

    const merge = handlePushRejected({
      localRecord,
      serverRecord,
      baseSnapshot: localRecord?.$snapshot ?? item.payload
    });

    if (merge.conflicts.length === 0) {
      // Auto-mergeable: write merged doc back with a bumped version.
      await this.pb.collection(pbCollection).update(
        remoteId,
        {
          ...mapLocalToRemote(item.collection, { ...localRecord, data: merge.merged }, remoteId),
          version: (serverRecord.version ?? 0) + 1
        },
        { $autoCancel: false }
      );
      await stampSynced(item, merge.merged);
    } else {
      // Human decision needed — park locally for review UI.
      await db.table(item.collection).update(key, {
        syncStatus: SYNC_STATUS.CONFLICT,
        $conflictMeta: {
          conflicts: merge.conflicts,
          serverSnapshot: serverRecord,
          mergedProposal: merge.merged,
          detectedAt: Date.now()
        }
      });
    }
  }

  // ---------------------------------------------------------------------
  // PULL — reconcile server changes since the tenant watermark
  // ---------------------------------------------------------------------

  async pullDeltas() {
    if (!this.activeTenantId || !navigator.onLine) return 0;

    const cursorKey = `${SYNC_CURSOR_KEY}:${this.activeTenantId}`;
    const since = await db.getMeta(cursorKey, 0);

    let applied = 0;
    let newest = since;

    for (const collection of Object.keys(COLLECTION_MAP)) {
      const pbCollection = COLLECTION_MAP[collection];
      let page = 1;
      for (;;) {
        const pageResult = await this.pb
          .collection(pbCollection)
          .getList(page, 100, {
            filter: `organization_id = "${this.activeTenantId}" && updated > "${isoFromMillis(since)}"`,
            sort: '-updated',
            $autoCancel: false
          });

        for (const remote of pageResult.items) {
          const didApply = await this.applyInbound(collection, remote);
          if (didApply) applied++;
          const t = Date.parse(remote.updated);
          if (t > newest) newest = t;
        }

        if (page >= pageResult.totalPages) break;
        page++;
      }
    }

    // Advance the watermark only AFTER every page landed in Dexie — a crash
    // mid-pull replays the same window idempotently instead of skipping it.
    if (newest > since) {
      await db.setMeta(cursorKey, newest);
      this.lastSyncAt = newest;
    }
    return applied;
  }

  /**
   * Apply one inbound server record to the local cache via the
   * ConflictResolver decision matrix.
   *
   * Cross-device identity: forms match on `instanceId`, customers and
   * properties on `remoteId` (Dexie v5) — both are the PB record id.
   */
  async applyInbound(collection, remote) {
    const table = db.table(collection);

    const local =
      collection === 'forms'
        ? await table.where('instanceId').equals(remote.id).first()
        : await table.where('remoteId').equals(remote.id).first();

    const decision = resolveInbound({
      local: local ?? null,
      server: remote,
      base: local?.$snapshot ?? null
    });

    switch (decision.action) {
      case 'take_server': {
        const row = mapRemoteToLocal(collection, remote, local);
        await table.put(row);
        return true;
      }
      case 'merge': {
        const row = {
          ...(local ?? {}),
          data: decision.result.merged,
          syncStatus: SYNC_STATUS.SYNCED,
          mutationVersion: (local?.mutationVersion ?? 0) + 1,
          updatedAt: new Date(),
          $snapshot: remote
        };
        await table.put(row);
        return true;
      }
      case 'conflict_review': {
        await table.update(local.id, {
          syncStatus: SYNC_STATUS.CONFLICT,
          $conflictMeta: {
            conflicts: decision.result.conflicts,
            serverSnapshot: remote,
            mergedProposal: decision.result.merged,
            detectedAt: Date.now()
          }
        });
        return true;
      }
      case 'ignore':
      default:
        return false;
    }
  }

  // ---------------------------------------------------------------------
  // SSE subscriptions (hints only — never load-bearing)
  // ---------------------------------------------------------------------

  async subscribeToTenantCollections() {
    if (!this.activeTenantId || !navigator.onLine) return;

    for (const [localName, pbName] of Object.entries(COLLECTION_MAP)) {
      try {
        const unsub = await this.pb
          .collection(pbName)
          .subscribe(
            '*',
            (e) => {
              // Cross-tenant guard: ignore events outside the active tenant.
              if (
                e.record?.organization_id &&
                e.record.organization_id !== this.activeTenantId
              ) {
                return;
              }
              scheduleReconcile(() => this.applyInbound(localName, e.record));
            },
            { $autoCancel: false }
          );
        this._unsubscribers.push(unsub);
      } catch (error) {
        console.warn(`SSE subscribe failed for ${pbName}:`, error.message);
      }
    }
  }

  teardownSubscriptions() {
    this._unsubscribers.forEach((unsub) => {
      try {
        unsub();
      } catch {
        /* noop */
      }
    });
    this._unsubscribers = [];
  }

  // ---------------------------------------------------------------------
  // Orchestration
  // ---------------------------------------------------------------------

  /**
   * Full reconcile cycle: push first (our changes win bandwidth priority),
   * then pull remote deltas behind the tenant watermark.
   */
  async reconcileNow(reason = 'manual') {
    if (!this.isRunning || this.isSyncing || !navigator.onLine) return;

    try {
      const push = await this.pushOutbox();
      const pulled = await this.pullDeltas();
      this.status = 'idle';
      this.lastSyncAt = await db.getMeta(
        `${SYNC_CURSOR_KEY}:${this.activeTenantId}`,
        this.lastSyncAt ?? 0
      );
      this._emit();
      return { ...push, pulled, reason };
    } catch (error) {
      console.error('reconcileNow failed:', error);
      this.status = navigator.onLine ? 'error' : 'offline';
      this._emit();
    }
  }
}

// -------------------------------------------------------------------------
// Module-level helpers
// -------------------------------------------------------------------------

function isoFromMillis(ms) {
  // 2s overlap absorbs minor client/server clock skew.
  return new Date(Math.max(ms - 2000, 0)).toISOString();
}

function isPreconditionFailed(err) {
  return (
    err?.status === 412 ||
    err?.response?.status === 412 ||
    (err?.status === 400 && /version/i.test(err?.response?.message ?? ''))
  );
}

function isDuplicateKeyError(err) {
  const msg = err?.response?.message ?? err?.message ?? '';
  return (
    err?.status === 400 &&
    /unique|duplicate|already exists/i.test(msg)
  );
}

function isNotFound(err) {
  return err?.status === 404 || err?.response?.status === 404;
}

function isAuthError(err) {
  return err?.status === 401 || err?.response?.status === 401;
}

function isRetryable(err) {
  // Validation errors (422) and auth failures won't heal on retry.
  return !(isAuthError(err) || err?.status === 422);
}

/**
 * Map a local Dexie record onto the PocketBase schema (snake_case), per
 * collection. The client-minted PB-format id rides as `id` so the server
 * record and local record share one immutable cross-device identity.
 * Local-only bookkeeping fields ($snapshot, syncStatus, mutationVersion,
 * tenantId, remoteId, lastSyncedAt) never reach the wire — organization_id
 * is stamped server-side by pb_hooks/security.pb.js.
 */
function mapLocalToRemote(collection, record, remoteId) {
  if (collection === 'forms') {
    return {
      id: remoteId,
      form_type: record.formId,
      certificate_number: record.serialNumber ?? null,
      status: record.status,
      data: record.data ?? {},
      version: record.mutationVersion ?? 1,
      client_updated_at: record.updatedAt?.toISOString?.()
        ?? new Date().toISOString(),
      deleted_at: record.deletedAt
        ? new Date(record.deletedAt).toISOString()
        : null
    };
  }

  if (collection === 'properties') {
    return {
      id: remoteId,
      customer_id: record.customerId ?? null,
      address_line1: record.addressLine1 ?? '',
      address_line2: record.addressLine2 ?? '',
      city: record.city ?? '',
      postcode: record.postcode ?? '',
      country: record.country ?? '',
      appliances: record.appliances ?? [],
      notes: record.notes ?? '',
      version: record.mutationVersion ?? 1,
      client_updated_at: record.updatedAt?.toISOString?.()
        ?? new Date().toISOString(),
      deleted_at: record.deletedAt
        ? new Date(record.deletedAt).toISOString()
        : null
    };
  }

  // customers
  return {
    id: remoteId,
    name: record.name ?? '',
    type: record.type ?? '',
    email: record.email ?? null,
    phone: record.phone ?? '',
    emails: record.emails ?? [],
    billing_address: record.billingAddress ?? '',
    notes: record.notes ?? '',
    version: record.mutationVersion ?? 1,
    client_updated_at: record.updatedAt?.toISOString?.()
      ?? new Date().toISOString(),
    deleted_at: record.deletedAt
      ? new Date(record.deletedAt).toISOString()
      : null
  };
}

/**
 * Post-push bookkeeping: stamp the source record SYNCED and store its
 * acknowledged payload as the next 3-way-merge ancestor ($snapshot).
 */
async function stampSynced(item, mergedData = null) {
  try {
    const table = db.table(item.collection);
    const numericId = Number(item.recordId);
    const key = Number.isFinite(numericId) ? numericId : String(item.recordId);
    const record = await table.get(key);
    if (!record) return;

    await table.put({
      ...record,
      syncStatus: SYNC_STATUS.SYNCED,
      mutationVersion: (record.mutationVersion ?? 0) + 1,
      lastSyncedAt: Date.now(),
      $snapshot: mergedData
        ? deepMergeSnapshots(record.$snapshot ?? {}, mergedData)
        : record.data ?? {}
    });
  } catch {
    /* best-effort bookkeeping — never fail the push for it */
  }
}

function deepMergeSnapshots(base, patch) {
  const out = structuredClone(base);
  for (const [k, v] of Object.entries(patch ?? {})) {
    if (isPlainObject(v) && isPlainObject(out[k])) {
      out[k] = deepMergeSnapshots(out[k], v);
    } else {
      out[k] = v;
    }
  }
  return out;
}

function isPlainObject(v) {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

/** Map a PocketBase record onto the local row shape, per collection. */
function mapRemoteToLocal(collection, remote, existing) {
  // New rows keep an undefined `id` so Dexie assigns the next ++id key —
  // writing remote.id (a 15-char string) into the numeric PK would corrupt
  // the table. Existing rows preserve their local numeric key.
  const common = {
    ...(existing ? { id: existing.id } : {}),
    tenantId: remote.organization_id,
    userId: remote.created_by ?? existing?.userId,
    syncStatus: SYNC_STATUS.SYNCED,
    deletedAt: remote.deleted_at ? new Date(remote.deleted_at) : null,
    updatedAt: new Date(remote.updated),
    createdAt: remote.created ? new Date(remote.created) : existing?.createdAt,
    mutationVersion: remote.version ?? existing?.mutationVersion ?? 1,
    $snapshot: remote
  };

  if (collection === 'forms') {
    return {
      ...common,
      formId: remote.form_type,
      instanceId: remote.id, // cross-device identity
      serialNumber: remote.certificate_number,
      status: remote.status,
      lastAccessedAt: existing?.lastAccessedAt ?? new Date(remote.updated),
      data: remote.data ?? {}
    };
  }

  if (collection === 'properties') {
    return {
      ...common,
      remoteId: remote.id, // cross-device identity (Dexie v5)
      customerId: remote.customer_id ?? existing?.customerId,
      addressLine1: remote.address_line1 ?? existing?.addressLine1,
      addressLine2: remote.address_line2 ?? existing?.addressLine2,
      city: remote.city ?? existing?.city,
      postcode: remote.postcode ?? existing?.postcode,
      country: remote.country ?? existing?.country,
      appliances: remote.appliances ?? existing?.appliances ?? [],
      notes: remote.notes ?? existing?.notes
    };
  }

  // customers
  return {
    ...common,
    remoteId: remote.id, // cross-device identity (Dexie v5)
    name: remote.name ?? existing?.name,
    type: remote.type ?? existing?.type,
    email: remote.email ?? '',
    phone: remote.phone ?? '',
    emails: remote.emails ?? existing?.emails ?? [],
    billingAddress: remote.billing_address ?? existing?.billingAddress ?? '',
    notes: remote.notes ?? existing?.notes
  };
}

// Trailing-edge debounce shared across SSE handlers — coalesces event
// bursts into one applyInbound pass instead of racing writes per event.
let reconcileTimer = null;
let pendingReconcileTasks = [];
function scheduleReconcile(task) {
  pendingReconcileTasks.push(task);
  clearTimeout(reconcileTimer);
  reconcileTimer = setTimeout(async () => {
    const tasks = pendingReconcileTasks.splice(0);
    for (const t of tasks) {
      try {
        await t();
      } catch (e) {
        console.warn('SSE reconcile task failed:', e.message);
      }
    }
  }, 300);
}
